import React, { Component } from 'react';
import { Box, Button, DataTable, Text, TextInput, FormField } from 'grommet';
import { compose } from 'recompose';
import * as moment from 'moment';

import { withFirebase } from '../Firebase';
import { bulkGift } from '../../lib/walletApi';

import './index.css';

const borderConfig = {
  header: {
    color: 'border',
    side: 'both',
    size: 'small',
  },
  body: {
    color: 'border',
    side: 'both',
    size: 'small',
  },
};

class WalletsPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      users: [],
      selected: [],
      creditsPerUser: 1,
      note: '',
      giftResult: null,
    };
  }

  async componentDidMount() {
    this.setState({ loading: true });

    const { firebase } = this.props;
    const [usersSnap, profilesSnap, walletsSnap] = await Promise.all([
      firebase.users().once('value'),
      firebase.profiles().once('value'),
      firebase.db.ref('wallets').once('value'),
    ]);

    const usersObject = usersSnap.val() || {};
    const profilesObject = profilesSnap.val() || {};
    const walletsObject = walletsSnap.val() || {};

    const users = Object.keys(usersObject).map((uid) => {
      const profile = profilesObject[uid] || {};
      const wallet = walletsObject[uid] || {};
      return {
        uid,
        alias: profile.alias || '—',
        last_active: profile.last_active,
        balance: typeof wallet.balance === 'number' ? wallet.balance : 0,
      };
    });

    this.setState({ users, loading: false });
  }

  refreshBalances = async () => {
    const walletsSnap = await this.props.firebase.db.ref('wallets').once('value');
    const wallets = walletsSnap.val() || {};
    this.setState((state) => ({
      users: state.users.map((u) => ({
        ...u,
        balance:
          wallets[u.uid] && typeof wallets[u.uid].balance === 'number'
            ? wallets[u.uid].balance
            : 0,
      })),
    }));
  };

  onSelect = (selected) => this.setState({ selected });

  onChangeCredits = (event) => this.setState({ creditsPerUser: event.target.value });

  onChangeNote = (event) => this.setState({ note: event.target.value });

  onGift = async () => {
    const { selected, creditsPerUser, note } = this.state;
    if (selected.length === 0) return;
    const credits = parseInt(creditsPerUser, 10);
    if (!Number.isFinite(credits) || credits <= 0) {
      this.setState({
        giftResult: { error: 'Credits per user must be a positive integer.' },
      });
      return;
    }

    this.setState({ giftResult: { pending: true } });
    try {
      const result = await bulkGift(this.props.firebase)({
        userIds: selected,
        creditsPerUser: credits,
        note,
      });
      await this.refreshBalances();
      this.setState({
        giftResult: result,
        selected: [],
      });
    } catch (err) {
      this.setState({ giftResult: { error: err.message || 'Gift failed' } });
    }
  };

  render() {
    const { users, selected, creditsPerUser, note, loading, giftResult } = this.state;

    return (
      <Box pad="medium" className="wallets-screen">
        {loading && <div>Loading ...</div>}

        <Box direction="row" justify="between" align="center">
          <h1>Wallets ({users.length})</h1>
        </Box>

        <DataTable
          sortable={true}
          step={50}
          primaryKey="uid"
          border={borderConfig}
          select={selected}
          onSelect={this.onSelect}
          sort={{
            direction: 'desc',
            property: 'last_active',
          }}
          columns={[
            {
              property: 'alias',
              header: <Text>Alias</Text>,
              search: true,
              sortable: true,
              size: 'small',
            },
            {
              property: 'uid',
              header: <Text>ID</Text>,
              search: true,
              sortable: false,
              size: 'large',
            },
            {
              property: 'balance',
              header: <Text>Balance</Text>,
              sortable: true,
              size: 'small',
              render: (datum) => <Text>{datum.balance}</Text>,
            },
            {
              property: 'last_active',
              header: <Text>Last Active</Text>,
              sortable: true,
              size: 'medium',
              render: (datum) => (
                <Text truncate="tip">
                  {datum.last_active
                    ? `${moment(datum.last_active).format('YYYY-MM-DD h:mm a')} (${moment(datum.last_active).fromNow()})`
                    : 'never'}
                </Text>
              ),
            },
          ]}
          data={users}
        />

        <Box
          direction="row"
          gap="medium"
          margin={{ top: 'medium' }}
          align="end"
          className="wallets-gift-form"
        >
          <FormField label="Credits per user">
            <TextInput
              type="number"
              min="1"
              value={creditsPerUser}
              onChange={this.onChangeCredits}
            />
          </FormField>
          <FormField label="Note">
            <TextInput value={note} onChange={this.onChangeNote} />
          </FormField>
          <Button
            primary
            label={`Gift ${selected.length} ${selected.length === 1 ? 'user' : 'users'}`}
            disabled={selected.length === 0 || (giftResult && giftResult.pending)}
            onClick={this.onGift}
          />
        </Box>

        {giftResult && (
          <Box margin={{ top: 'medium' }} gap="xsmall">
            {giftResult.pending && <Text>Gifting…</Text>}
            {giftResult.error && (
              <Text color="status-critical">Error: {giftResult.error}</Text>
            )}
            {giftResult.gift_batch_id && (
              <Box gap="xsmall">
                <Text color="status-ok">
                  Gifted {(giftResult.succeeded || []).length}{' '}
                  {(giftResult.succeeded || []).length === 1 ? 'user' : 'users'} (batch{' '}
                  {giftResult.gift_batch_id}).
                </Text>
                {giftResult.failed && giftResult.failed.length > 0 && (
                  <Box gap="xsmall">
                    <Text color="status-warning">
                      {giftResult.failed.length} failed. Some may have been credited but missing a
                      ledger row — check the affected user's wallet panel before retrying.
                    </Text>
                    {giftResult.failed.map((f) => (
                      <Text key={f.user_id} size="small">
                        {f.user_id}:{' '}
                        {f.credited ? 'credited (ledger row missing)' : 'not credited'}
                        {f.error ? ` — ${f.error}` : ''}
                      </Text>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }
}

export default compose(withFirebase)(WalletsPage);
